import { getXmlAttribute, getXmlElement } from '@studio/utils/dom-utils';
import { DASH_REPRESENTATION_ID_TEMPLATE } from './manifest.const';
export function parseDashManifest(manifest) {
    const parser = new DOMParser();
    const xml = parser.parseFromString(manifest, 'text/xml');
    const mpd = getMPD(xml);
    const totalDuration = getTotalDuration(mpd);
    const maxSegmentDuration = getMaxSegmentDuration(mpd);
    const totalChunks = Math.ceil(totalDuration / maxSegmentDuration);
    const representations = Array.from(xml.querySelectorAll('Representation'));
    const videoRepresentations = representations
        .filter(representation => getMimeType(representation) === 'video/mp4')
        .map(representation => mapToStreamingRepresentation(representation))
        .sort(sortRepresentationByBandwidth);
    const audioRepresentations = representations
        .filter(representation => getMimeType(representation) === 'audio/mp4')
        .map(representation => mapToStreamingRepresentation(representation));
    const videoCodecs = videoRepresentations.length ? videoRepresentations[0].codecs : '';
    const audioCodecs = audioRepresentations.length ? audioRepresentations[0].codecs : '';
    return {
        totalDuration,
        totalChunks,
        representations: {
            audio: audioRepresentations,
            video: videoRepresentations
        },
        codecs: {
            audio: audioCodecs,
            video: videoCodecs
        }
    };
}
export function mapToStreamingRepresentation(representation) {
    const adaptationSet = representation.parentElement;
    if (!adaptationSet) {
        throw new Error('No AdaptationSet element found next to the representation');
    }
    const isVideo = getMimeType(representation) === 'video/mp4';
    const bandwidth = +getXmlAttribute(representation, 'bandwidth');
    const id = getXmlAttribute(representation, 'id');
    const identifier = isVideo
        ? `${representation.getAttribute('height')}p`
        : representation.getAttribute('audioSamplingRate');
    const codecs = getInheritedXmlAttribute(representation, 'codecs');
    const name = `${identifier}, ${Math.floor(bandwidth / 1000)} kbps`;
    const segmentTemplate = getXmlElement(adaptationSet, 'SegmentTemplate');
    const duration = +getXmlAttribute(segmentTemplate, 'duration');
    const timescale = +getXmlAttribute(segmentTemplate, 'timescale');
    const startIndex = +getXmlAttribute(segmentTemplate, 'startNumber');
    const representationChunkUrlTemplate = getXmlAttribute(segmentTemplate, 'media');
    const chunkUrlTemplate = representationChunkUrlTemplate.replace(DASH_REPRESENTATION_ID_TEMPLATE, id);
    const initTemplate = getXmlAttribute(segmentTemplate, 'initialization');
    const initUrl = initTemplate.replace(DASH_REPRESENTATION_ID_TEMPLATE, id);
    return {
        id,
        name,
        duration: duration / timescale,
        chunkUrlTemplate,
        initUrl,
        bandwidth,
        startIndex,
        codecs
    };
}
function getMimeType(representation) {
    var _a, _b, _c;
    return ((_c = (_a = representation.getAttribute('mimeType')) !== null && _a !== void 0 ? _a : (_b = representation.parentElement) === null || _b === void 0 ? void 0 : _b.getAttribute('mimeType')) !== null && _c !== void 0 ? _c : null);
}
function getInheritedXmlAttribute(representation, attribute) {
    var _a, _b;
    const value = (_a = representation.getAttribute(attribute)) !== null && _a !== void 0 ? _a : (_b = representation.parentElement) === null || _b === void 0 ? void 0 : _b.getAttribute(attribute);
    if (!value) {
        throw new Error(`No ${attribute} attribute found in the Representation or its AdaptationSet`);
    }
    return value;
}
function sortRepresentationByBandwidth(repA, repB) {
    return repA.bandwidth - repB.bandwidth;
}
export function getMPD(manifest) {
    const mpd = manifest.querySelector('MPD');
    if (!mpd) {
        throw new Error('No MPD element found in the manifest');
    }
    return mpd;
}
export function getMediaPresentationDurationInSeconds(duration) {
    const match = duration.match(/PT(\d+H)?(\d+M)?(\d+(\.\d+)?S)?/);
    if (!match) {
        throw new Error('Invalid duration format');
    }
    const hours = parseFloat(match[1]) || 0;
    const minutes = parseFloat(match[2]) || 0;
    const seconds = parseFloat(match[3]) || 0;
    return hours * 3600 + minutes * 60 + seconds;
}
export function getTotalDuration(mpd) {
    const mediaPresentationDuration = getXmlAttribute(mpd, 'mediaPresentationDuration');
    return getMediaPresentationDurationInSeconds(mediaPresentationDuration);
}
export function getMaxSegmentDuration(mpd) {
    const maxSegmentDuration = getXmlAttribute(mpd, 'maxSegmentDuration');
    return getMediaPresentationDurationInSeconds(maxSegmentDuration);
}
